import { useState, useMemo } from "react";
import { ActionIcon, Badge, Button, Group, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconX } from "@tabler/icons-react";
import SelectCard from "../_Common/SelectedCard";
import ReactTimeago from "react-timeago-i18n";
import UserAvatar from "../_Common/UserAvatar";
import { createVersion } from "../api";
import useFlowStore from "./flowStore";

function ActiveEnvs({ flow, version, onUnpin }) {
  if (!flow || !version) return null;
  if (!flow.activeVersions) return null;
  const activeKeys = Object.keys(flow.activeVersions);
  let matchingKeys = activeKeys.filter(
    (key) => flow.activeVersions[key] === version.id
  );

  const askUnpin = (env) => {
    modals.openConfirmModal({
      title: `Unpin '${env}'?`,
      children: (
        <Text size="sm">
          This removes '{env}' from this flow only. The version stays. Requests
          that ask for '{env}' on this flow will no longer resolve a version.
        </Text>
      ),
      labels: { confirm: "Unpin", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => onUnpin(env),
    });
  };

  return (
    <>
      {matchingKeys.map((env) => (
        <Badge
          mr="xs"
          mb={4}
          maw="100%"
          key={"active-env" + env}
          size="sm"
          color="lime"
          style={{ maxWidth: "100%" }}
          rightSection={
            onUnpin ? (
              <ActionIcon
                size="xs"
                variant="transparent"
                color="lime"
                aria-label={`Unpin ${env}`}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  askUnpin(env);
                }}
              >
                <IconX size={10} />
              </ActionIcon>
            ) : null
          }
        >
          {env}
        </Badge>
      ))}
    </>
  );
}

// FIXME: This is b0rken... The selected version state ALSO needs sharing, which means pretty much all of this component's
// state is shared, which means ... this is not a separate component...
export default function FlowTemplateVersions({
  flow,
  selectedVersion,
  setSelectedVersion,
  onUnpin,
}) {
  const { flowTemplates, setFlowTemplates } = useFlowStore();
  const [showAll, setShowAll] = useState(false);

  // Compute relevant version IDs for filtering
  const { displayedVersions, hiddenCount } = useMemo(() => {
    const relevantIds = new Set();

    // Add activated versions
    if (flow?.activeVersions) {
      Object.values(flow.activeVersions).forEach((id) => relevantIds.add(id));
    }

    // Add parents of activated versions
    flowTemplates
      .filter((v) => relevantIds.has(v.id) && v.parentVersion)
      .forEach((v) => relevantIds.add(v.parentVersion));

    // Add editable drafts
    flowTemplates.filter((v) => v.isEditable).forEach((v) => relevantIds.add(v.id));

    const displayed = showAll
      ? flowTemplates
      : flowTemplates.filter((v) => relevantIds.has(v.id));
    const hidden = flowTemplates.length - displayed.length;

    return { displayedVersions: displayed, hiddenCount: hidden };
  }, [flowTemplates, flow?.activeVersions, showAll]);

  return (
    <Stack>
      {flowTemplates.length === 0 && (
        <>
          <Text c="dimmed" size="sm">
            No versions available. Create a new version to start.
          </Text>

          <Button
            onClick={() =>
              createVersion(flow.id, { name: "New Version" }).then((data) => {
                setFlowTemplates([data, ...flowTemplates]);
                setSelectedVersion(data);
              })
            }
            variant="light"
          >
            Create Version
          </Button>
        </>
      )}

      {displayedVersions.map((p) => (
        <SelectCard
          key={p.id}
          onClick={() => {
            setSelectedVersion(p);
          }}
          selected={selectedVersion?.id == p.id}
        >
          <Stack gap="xs">
            <Group justify="space-between">
              {p.user && <UserAvatar profile={p.user} />}
              <Text fw={500}>
                <ReactTimeago date={p.created} />
              </Text>
            </Group>

            {p.comment && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {p.comment}
              </Text>
            )}

            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <div style={{ minWidth: 0, flex: 1 }}>
                <ActiveEnvs flow={flow} version={p} onUnpin={onUnpin} />
              </div>
              <Badge color="dark" radius="xs" size="sm">
                {p.templates.length} templates
              </Badge>
            </Group>
          </Stack>
        </SelectCard>
      ))}

      {hiddenCount > 0 && (
        <Button
          variant="subtle"
          size="xs"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? "Show less" : `Show ${hiddenCount} more`}
        </Button>
      )}
    </Stack>
  );
}
